<template lang="pug">
.screen-result
  template(v-if="isValidData && aggregateStatus.completed && !wordDoc && !audioDoc")
    uc-screen(
      :data-queue-audio="result.audio"
      :edit-path="result.threshold === 'passed' || result.threshold === 'failed' || result.threshold === 'exceeded-attempts' ? `${editPath}.${result.threshold}.0` : `${editPath}.when-at-least.${result.threshold}.0`"
      :key="result.threshold"
      :nested="true"
      :screen="{ 'no-fade': screen['no-fade'] || aggregateStatus.passed ? true : false, ...screen }"
      v-for="screen in result.screens"
    )

  //- render all conditional results (if any) for word docs and audio docs
  div(v-for="[k, v] in allThresholds" v-if="wordDoc || audioDoc")
    audio-marker(
      :name="`${screen.id}-${k}`"
      v-if="k === 'passed' || k === 'failed' || k === 'exceeded-attempts'"
    )
    audio-marker(:name="`${screen.id}-score-${k}`" v-else="")
    uc-text(:k="`${v.id}.text`" :text="v.text" type="p")

.screen-result-reset(v-if="aggregateStatus.failed && !exceededAttempts")
  uc-button.btn.btn-primary(@click="reset" text="Try again" data-sfx="continue")

//- .screen-result-continue(v-if="aggregateStatus.failed")
//-   uc-button.btn.btn-primary(@click="reset" text="Try again")
//- //- TODO put this in a slot?
//- uc-screen.retry
//-   uc-button(@click="reset" text="Try again")
.screen-result-exceeded(v-if="exceededAttempts")
  uc-button.btn.btn-primary(
    @click="exitCourse"
    data-sfx="close"
    text="Close"
    v-if="!hasSessionEnded"
    :disabled="hasSessionEnded"
  )
  .close-message-container.w-full.flex.justify-center.items-center.mt-10(
    v-if="hasSessionEnded"
  )
    .screen-exit-message.text-center.border-radius.border-b-secondary-500.transition.opacity-90.bg-paper.p-6
      uc-text.text-xl.editable.editable--prose.prose(
        text="If the window does not automatically close, please close it manually."
      )
</template>

<script setup lang="ts">
import {
  computed,
  inject,
  watchEffect,
  ref,
  nextTick,
  watch,
  toRef,
  withDefaults,
  type Ref
} from 'vue';
import useQuizzes from '../stores/useQuizzes';
import useSuspend from '../lib/useSuspend';
import { setCompleted, set, setExit, getLessonStatus } from '../lib/SCORM';
import { play, queue, stop, nowPlaying } from '../lib/audio';
import useAudio from '../stores/useAudio';
import useScreenAudio, {
  useChildScreenAudio,
  type NarratableScreen
} from '../lib/useScreenAudio';
import { schemaForTemplate } from '../lib/screen-schemas/index';
import bugAlert from '../lib/bugAlert';
import { storeToRefs } from 'pinia';
import useContent from '../stores/useContent';
import { getPassmark, setSessionTime } from '../lib/SCORM';
import useTranslations from '../stores/useTranslations';

const suspend = useSuspend();
const quizzes = useQuizzes();
const { translations } = useTranslations();
const { getQuizUnits } = useQuizzes();
const { computeQuizQuestionStatuses } = storeToRefs(useQuizzes());
const { compiled } = storeToRefs(useContent());
const content = compiled;
const emit = defineEmits(['exit']);

const wordDoc = inject('wordDoc') as boolean;
const audioDoc = inject('audioDoc') as boolean;
const goToSection = inject('goToSection');
const pageId = inject('pageId') as string;

const { getAudioURL } = useAudio();
const { resolveChildIntro, audioAiJitEnabled } = useChildScreenAudio();
const sectionActive = inject<Ref<boolean>>('sectionActive', ref(false));

const scormPassmark = getPassmark();

const reset = async () => {
  stop();

  const app = document.querySelector('#app');
  // send event to reset course and unit title animations
  app?.dispatchEvent(new CustomEvent('restartTitleAnim'));

  // scroll to the first quiz page
  const firstQuizPage =
    computedUnitsToReset.value[0] || content.value.pages[0].id;
  if (pageId === firstQuizPage) {
    const scroller = document.querySelector('.scroller') as HTMLElement;
    gsap.to(scroller, {
      duration: 0,
      scrollTo: 0
    });
  } else {
    const firstScreenofFirstPage =
      content.value.pages.find(page => page.id === firstQuizPage)?.screens[0] ||
      content.value.pages[0].screens[0];
    await goToSection.value(firstQuizPage, firstScreenofFirstPage.id, {
      duration: 0
    });
  }

  // reset component level custom suspend data and animations
  const clonedQuizzes = includedQuizzes.value.slice();
  app?.dispatchEvent(
    new CustomEvent('quizReset', { detail: { quizzes: clonedQuizzes } })
  );

  // generic reset for all screens on quiz pages
  await resetAllQuizUnits();

  // reset suspend for all included quiz questions and selections
  const quizzesFromStore = computeQuizQuestionStatuses.value;
  includedQuizzes.value.forEach(q => {
    if (!quizzesFromStore.has(q)) return;
    quizzesFromStore?.get(q)?.forEach(question => {
      const id = question?.id;
      set(`completed.${id}`, false);
      set(`correctQuestions.${id}`, false);

      if (suspend.questionOptions[id]) {
        set(`questionOptions.${id}`, false);
      }
    });
  });

  await nextTick();

  // reset the new page
  const page = document.querySelector('.page') as HTMLElement;
  page.querySelectorAll('.section').forEach((section, index) => {
    if (section.id && index !== 0) {
      set(`completed.${section.id}`, false);
    }
  });

  page.refreshSections();
};

const props = withDefaults(
  defineProps<{
    editPath: string;
    screen: UcScreen & {
      'for-quiz': string;
      passmark?: number;
      passed?: UcScreen[];
      failed?: UcScreen[];
      'max-attempts'?: number;
      'exceeded-attempts'?: UcScreen[];
      'when-at-least'?: {
        [score: number]: UcScreen[];
      };
    };
    /** Ancestor owns section intro when nested under `uc-screen` / `templates[]`. */
    nested?: boolean;
  }>(),
  { nested: false }
);

const includedQuizzes = computed(() => props.screen['for-quiz'].split(/\s+/));

// screen-result.schema.ts has managesOwnSectionAudio:true — we own the full
// sequence (intro → result child screens). resolveSlot gives us the intro URL.
const resultSchema = schemaForTemplate('result');
const { resolveSlot } = useScreenAudio(toRef(props, 'screen'), resultSchema);

// get all the pages that contain the quizzes set by for-quiz on the screen
const computedUnitsToReset = computed(() => {
  const pageArray: string[] = [];
  includedQuizzes.value.forEach(q => {
    // getQuizUnits returns a Set
    getQuizUnits(q)?.forEach(unit => {
      if (!pageArray.includes(unit)) {
        pageArray.push(unit);
      }
    });
  });
  return pageArray;
});

const resetAllQuizUnits = async () => {
  computedUnitsToReset.value.forEach(async unit => {
    const pageContent = content.value.pages?.find(page => page.id === unit);
    set(`completed.${unit}`, false);
    pageContent?.screens.forEach((screen, index) => {
      // skip the title screen of the page
      if (index === 0) return;
      set(`completed.${screen.id}`, false);
      // reset the template array for split screens, both left and right
      if (
        screen.template === 'split' ||
        screen.template === 'split-2-1' ||
        screen.template === 'split-1-2' ||
        screen.template === 'sticky-left' ||
        screen.template === 'sticky-right'
      ) {
        if (screen.left) resetTemplateArray(screen.left);
        if (screen.right) resetTemplateArray(screen.right);
        if (screen['left-edge']) resetTemplateArray(screen['left-edge']);
        if (screen['right-edge']) resetTemplateArray(screen['right-edge']);
      }
    });
  });
};

const resetTemplateArray = templates => {
  templates.forEach(template => {
    set(`completed.${template.id}`, false);
    if (suspend.questionOptions[template.id]) {
      set(`questionOptions.${template.id}`, false);
    }
  });
};

// aggregate the status of all included quizzes
const aggregateStatus = ref<QuizStatus>({
  completed: false,
  score: 0,
  submitted: 0,
  correct: 0,
  total: 0
});

// this computed passmark is used if we don't have a passmark specifically set on the results screen
const passmark = computed(() => {
  return props.screen.passmark || scormPassmark || 50;
});

watchEffect(() => {
  const { ...statuses } = quizzes.statuses;

  const aggr: QuizStatus = {
    completed: true,
    score: 0,
    submitted: 0,
    correct: 0,
    total: 0
  };

  let passed = true;

  includedQuizzes.value.forEach(q => {
    const quiz = statuses[q];

    if (quiz) {
      aggr.completed = aggr.completed && quiz.completed;
      aggr.score += quiz.score;
      aggr.submitted += quiz.submitted;
      aggr.correct += quiz.correct;
      aggr.total += quiz.total;

      // if there is no passmark specifically set on the results screen, fail if any quiz failed
      if (!props.screen.passmark && quiz.failed) {
        passed = false;
      }
    }
  });

  // if there are multiple quizzes average the score
  if (includedQuizzes.value.length > 1)
    aggr.score = Math.round((aggr.correct / aggr.total) * 100);

  // if there is a passmark specifically set on the results screen, fail if the score is below it
  if (props.screen.passmark && aggr.completed) {
    passed = aggr.score >= props.screen.passmark;
  }

  if (aggr.completed) {
    aggr.passed = passed;
    aggr.failed = !passed;
  }

  aggregateStatus.value = aggr;
});

/**
 * True when any included quiz has failed and reached the
 * max-attempts limit set on this result screen, or when
 * the persisted exceededAttempts flag is set (survives resume).
 */
const exceededAttempts = computed(() => {
  const max = props.screen['max-attempts'];
  if (!max) return false;

  return includedQuizzes.value.some(quizId => {
    const status = quizzes.statuses[quizId];
    // Check persisted flag first (accurate immediately on resume)
    if (status?.exceededAttempts) return true;
    // Then check live state
    return max ? status?.failed && (status?.attempts || 0) >= max : false;
  });
});

const hasSessionEnded = ref(false);

// Fail the course via SCORM once when exceeded-attempts is first triggered
const courseAlreadyFailed = ref(getLessonStatus() === 'failed');
watchEffect(() => {
  if (
    exceededAttempts.value &&
    aggregateStatus.value.completed &&
    !courseAlreadyFailed.value
  ) {
    courseAlreadyFailed.value = true;

    // Persist the exceededAttempts flag on each quiz that hit the limit
    const max = props.screen['max-attempts'];
    includedQuizzes.value.forEach(quizId => {
      const status = quizzes.statuses[quizId];
      if (status?.exceededAttempts) return; // already flagged (e.g. from persisted data)
      if (max && status?.failed && (status?.attempts || 0) >= max) {
        quizzes.setQuizExceededAttempts(quizId);
      }
    });

    quizzes.failCourse();
  }
});

const exitCourse = () => {
  console.log(
    `Exit course - exceeded quiz max-attempts: for-quiz: '${props.screen['for-quiz']}' max-attempts: ${props.screen['max-attempts']}`
  );
  stop();
  setSessionTime();
  nextTick(() => {
    setExit();
    emit('exit');
  });
  hasSessionEnded.value = true;
};

const isValidData = computed(() => {
  //check for passmark issues. If there is no passmark, and using when-at-least: values, then there is a problem.
  if (!props.screen.passmark && props.screen['when-at-least']) {
    bugAlert(
      `Screen #${props.screen.id}\n\nYou are using when-at-least: values for feedback but have not set a passmark. You should set a passmark for a matching when-at-least: value. The passmark for-quiz: '${props.screen['for-quiz']}' is ${passmark.value}.`
    );
  }

  return true;
});

const hasTranslation = (
  key: string | number,
  fallback: string | number = key
) => {
  // get the translation if it exists
  let maybeTranslated =
    translations?.[suspend.locale]?.[key] ||
    translations?.[suspend.locale]?.[fallback];
  return maybeTranslated || fallback || key;
};

const result = computed(() => {
  const s3Audio = (key: string) =>
    audioAiJitEnabled.value ? null : getAudioURL(key);

  if (aggregateStatus.value.passed && props.screen.passed) {
    return {
      screens: props.screen.passed,
      threshold: 'passed',
      audio: s3Audio(`${props.screen.id}-passed`)
    };
  }

  // Exceeded attempts takes priority over normal failed content
  if (exceededAttempts.value) {
    // expose these strings for translations
    // text="Unfortunately, you didn't answer enough questions correctly to achieve the pass mark. As you have no more attempts remaining for this quiz, the course has been marked as failed."
    // text="You have no more attempts remaining. Please close this course."
    const defaultScreen = {
      id: `${props.screen.id}-exceeded-attempts-default`,
      text: hasTranslation(
        "Unfortunately, you didn't answer enough questions correctly to achieve the pass mark. As you have no more attempts remaining for this quiz, the course has been marked as failed."
      ),
      instructions: hasTranslation(
        'You have no more attempts remaining. Please close this course.'
      )
    };
    return {
      screens: props.screen['exceeded-attempts'] || [defaultScreen],
      threshold: 'exceeded-attempts',
      audio: s3Audio(`${props.screen.id}-exceeded-attempts`)
    };
  }

  if (aggregateStatus.value.failed && props.screen.failed) {
    return {
      screens: props.screen.failed,
      threshold: 'failed',
      audio: s3Audio(`${props.screen.id}-failed`)
    };
  }

  // list all thresholds in descending order
  const thresholds = [
    ...Object.keys(props.screen['when-at-least'] ?? {}).map(Number),
    passmark.value,
    0
  ].sort((a, b) => b - a);

  // find the highest threshold that is met
  const foundTreshold = thresholds.find(t => {
    return aggregateStatus.value.score >= t;
  }) as number;

  if (foundTreshold === passmark.value && props.screen.passed) {
    return {
      screens: props.screen.passed,
      threshold: 'passed',
      audio: s3Audio(`${props.screen.id}-passed`)
    };
  }

  if (foundTreshold === 0 && props.screen.failed) {
    return {
      screens: props.screen.failed,
      threshold: 'failed',
      audio: s3Audio(`${props.screen.id}-failed`)
    };
  }

  return {
    screens: props.screen['when-at-least']?.[foundTreshold],
    threshold: foundTreshold,
    audio: s3Audio(`${props.screen.id}-score-${foundTreshold}`)
  };
});

// Resolve all result child screen intro URLs in parallel.
// Child screens typically have no id in the YAML, so we synthesise a stable
// deterministic id from parent id + threshold + index for the JiT cache key.
async function resolveResultUrls(
  screens: NarratableScreen[],
  threshold: string | number
): Promise<string[]> {
  const screensWithIds = screens.map((s, i) =>
    s.id ? s : { ...s, id: `${props.screen.id}-${threshold}-${i}` }
  );
  const resolved = await Promise.all(
    screensWithIds.map(s => resolveChildIntro(s))
  );
  return resolved.filter(Boolean) as string[];
}

// Play audio in a coordinated sequence: intro first, then result child screens.
// Resolves intro + child URLs in parallel for efficiency. If the intro resolves
// to a URL we play it first and queue the child screens after so they follow on
// naturally when the intro ends. If there is no intro text, we play the first
// child screen directly and queue the rest.
async function playResultSequence(
  resultScreens: NarratableScreen[],
  threshold: string | number
) {
  const [introUrl, resultUrls] = await Promise.all([
    resolveSlot('intro'),
    resultScreens?.length
      ? resolveResultUrls(resultScreens, threshold)
      : Promise.resolve([])
  ]);

  if (introUrl) {
    play(introUrl);
    resultUrls.forEach(url => queue(url));
  } else if (resultUrls.length) {
    play(resultUrls[0]);
    resultUrls.slice(1).forEach(url => queue(url));
  }
}

// JiT: section activates — play intro then queue result child screens.
// screen-result.schema.ts has managesOwnSectionAudio:true so the parent
// uc-screen's generic sectionActive watcher is suppressed — we own the sequence.
// The S3 path is handled by data-queue-audio attributes on the child uc-screens.
watch(sectionActive, async active => {
  if (props.nested) return;
  if (!active || !audioAiJitEnabled.value) return;
  await playResultSequence(
    (result.value?.screens ?? []) as NarratableScreen[],
    result.value?.threshold ?? ''
  );
});

// JiT: quiz completes while this section is already active.
// The intro may still be playing — queue result child screens to follow it.
// If the intro has already finished (nowPlaying() is null), play immediately.
//
// Guard: only fire when the quiz is actually complete. During reset(),
// resetAllQuizUnits() clears quiz statuses which changes `result` while
// sectionActive is still true — without this guard the watcher would play
// result audio again immediately before the page navigates back to the quiz.
watch(result, async newResult => {
  if (!newResult?.screens?.length || !sectionActive.value) return;
  if (!audioAiJitEnabled.value) return;
  if (!aggregateStatus.value.completed) return;
  const urls = await resolveResultUrls(
    newResult.screens as NarratableScreen[],
    newResult.threshold
  );
  if (!urls.length) return;
  // Re-check after the async JiT request — a reset may have completed and
  // cleared aggregateStatus while the network request was in-flight.
  if (!aggregateStatus.value.completed || !sectionActive.value) return;
  if (nowPlaying()) {
    urls.forEach(url => queue(url));
  } else {
    play(urls[0]);
    urls.slice(1).forEach(url => queue(url));
  }
});

const allThresholds = computed(() => {
  const allThresholds = [];
  if (props.screen.passed)
    allThresholds.push(['passed', props.screen.passed[0]]);
  if (props.screen.failed)
    allThresholds.push(['failed', props.screen.failed[0]]);
  if (props.screen['exceeded-attempts'])
    allThresholds.push([
      'exceeded-attempts',
      props.screen['exceeded-attempts'][0]
    ]);

  if (props.screen['when-at-least']) {
    Object.entries(props.screen['when-at-least']).forEach(score => {
      allThresholds.push([score[0], score[1][0]]);
    });
  }

  return allThresholds;
});

const sectionId = inject('sectionId') as string;

watchEffect(() => {
  if (aggregateStatus.value.passed) {
    setCompleted(sectionId);
  }
});
</script>

<style>
.screen-result-reset + .screen-next-button,
.screen-result-exceeded + .screen-next-button {
  display: none;
}
</style>
